import { NgClass } from '@angular/common';
import type {
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { Clipboard } from '@freelancer/clipboard';
import { ShareServiceHelper } from '@freelancer/share-helper';
import { Timer, TimeUtils } from '@freelancer/time-utils';
import { textVariants } from '@freelancer/ui/text';
import { ButtonComponent } from '../button/button.component';
import {
  flFacebookFilled,
  flFacebookMessenger,
  flLink,
  flLinkedinFilled,
  flReddit,
  flTelegram,
  flX,
  HoverColor,
  IconColor,
  IconName,
  IconSize,
  provideFlIcons,
} from '../icon';
import { IconModule } from '../icon/icon.module';
import { Margin } from '../margin';
import { FontColor, TextAlign, TextSize } from '../text';
import type { ShareIcons } from './share-buttons.types';
import {
  LIST_OF_SOCIAL_MEDIA_INFO,
  ShareButtonSizes,
  ShareButtonSpacing,
} from './share-buttons.types';

interface ShareButton {
  readonly buttonClassName: string;
  readonly displayText: string;
  readonly iconClassName: string;
  readonly iconName: IconName;
  readonly isCopyLink: boolean;
  readonly labelText: string;
  readonly shareUrl: string;
}

type ShareUrlDictionary = { [key in ShareIcons]: string };

const COPIED_TOOLTIP_DURATION_MS = 2000;

@Component({
  standalone: true,
  selector: 'fl-share-buttons',
  imports: [ButtonComponent, IconModule, MatTooltipModule, NgClass],
  template: `
    <!--
    Had to purposely move away from fl-button inputs because
    it seems that the css on classes gets overruled by the css on
    inputs when this component is used in share modal webapp compat.
    Also, all classes here are prefixed with ShareButton- to avoid
    css conflicts with legacy css.
    -->
    @for (button of shareButtons; track trackByIconName($index, button)) {
      <div
        class="Share-container"
        [class.Share-container--copyLink]="button.isCopyLink"
      >
        @if (button.isCopyLink) {
          <!--
          Empty anchor for the "Copied!" tooltip so it can be shown
          programmatically on copy without hijacking the button's own hover
          behaviour. Positioned over the copy button so the tooltip renders
          above it.
          -->
          <div
            #copiedTooltip="matTooltip"
            class="Share-copiedTooltipAnchor"
            i18n-matTooltip="Share link copied tooltip"
            matTooltip="Copied!"
            [matTooltipPosition]="'above'"
          ></div>
          <fl-button
            [class]="getMarginClassString()"
            [ngClass]="button.buttonClassName"
            [class.mb-xxsmall]="showText"
            [class.mb-none]="!showText"
            [display]="breakCopyLinkOnMobile ? 'block' : 'inline'"
            [displayTablet]="breakCopyLinkOnMobile ? 'inline' : undefined"
            (click)="onCopyClick(button.shareUrl)"
            (mouseleave)="resetCopiedTooltip()"
          >
            <span class="Share-copyLinkContent">
              <fl-icon
                class="Share-icon"
                [color]="IconColor.INHERIT"
                [hoverColor]="HoverColor.CURRENT"
                [label]="button.labelText"
                [name]="'ui-link'"
                [ngClass]="button.iconClassName"
                [size]="iconSize"
                [useIconFont]="true"
              />
              <span class="Share-copyLinkLabel">{{ button.displayText }}</span>
            </span>
          </fl-button>
        } @else {
          <fl-button
            [class]="getMarginClassString()"
            [ngClass]="button.buttonClassName"
            [link]="button.shareUrl"
            [class.mb-xxsmall]="showText"
            [class.mb-none]="!showText"
            (click)="onClick()"
          >
            <fl-icon
              class="Share-icon"
              [color]="IconColor.INHERIT"
              [hoverColor]="HoverColor.CURRENT"
              [label]="button.labelText"
              [name]="button.iconName"
              [ngClass]="button.iconClassName"
              [size]="iconSize"
              [useNgIcon]="true"
            />
          </fl-button>
        }
        @if (showText) {
          <span
            class="Share-text"
            [ngClass]="shareTextClasses"
          >
            {{ button.displayText }}
          </span>
        }
      </div>
    }
  `,
  styleUrls: ['./share-buttons.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    provideFlIcons({
      flFacebookFilled,
      flFacebookMessenger,
      flLink,
      flLinkedinFilled,
      flReddit,
      flTelegram,
      flX,
    }),
  ],
})
export class ShareButtonsComponent implements OnChanges, OnDestroy, OnInit {
  private clipboard = inject(Clipboard);
  private shareServiceHelper = inject(ShareServiceHelper);
  private timeUtils = inject(TimeUtils);

  HoverColor = HoverColor;
  IconColor = IconColor;

  @ViewChild('copiedTooltip') copiedTooltip?: MatTooltip;
  private copiedTooltipTimeoutId?: Timer;

  @Input() set size(value: ShareButtonSizes) {
    this.iconSize = this.getIconSize(value);
  }

  @Input() set sizeTablet(value: ShareButtonSizes) {
    this.iconSizeTablet = this.getIconSize(value);
  }

  @Input() set sizeDesktop(value: ShareButtonSizes) {
    this.iconSizeDesktop = this.getIconSize(value);
  }

  @HostBinding('attr.data-spacing')
  @Input()
  shareButtonSpacing: ShareButtonSpacing = 'space-evenly';

  @Input() shareUrl: string;
  @Input() shareTitle?: string;
  @Input() shareText?: string;
  @Input() shareIcons: readonly ShareIcons[] = [
    'facebook',
    'twitter',
    'linkedin',
    'reddit',
    'telegram',
  ];

  @Input() textColor: FontColor = FontColor.FOREGROUND;

  @Input() showText = true;

  @HostBinding('class.Share--hideTextMobile')
  @Input()
  hideTextOnMobile = false;

  /**
   * Wraps the copy-link button onto its own row below the tablet breakpoint
   * while keeping it inline with the other buttons on larger screens.
   */
  @HostBinding('class.Share--breakCopyLinkMobile')
  @Input()
  breakCopyLinkOnMobile = false;

  @Input() buttonRightMargin: Margin = 'none';
  @Input() buttonRightTabletMargin?: Margin;
  @Input() buttonRightDesktopMargin?: Margin;
  @Input() buttonRightDesktopLargeMargin?: Margin;

  @Input() excludeShareTracking = false;

  /**
   * Required to build the Messenger share URL. When the `messenger` icon is
   * requested without an app id, the button falls back to the base share link.
   */
  @Input() facebookAppId?: string;

  @Output() onButtonClick = new EventEmitter<void>();
  @Output() onCopyLink = new EventEmitter<void>();

  iconSize: IconSize = IconSize.MID;
  iconSizeDesktop: IconSize;
  iconSizeTablet: IconSize;
  shareButtons: readonly ShareButton[];

  shareTextClasses: string;

  ngOnInit(): void {
    this.computeClasses();
  }

  ngOnDestroy(): void {
    clearTimeout(this.copiedTooltipTimeoutId);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      'excludeShareTracking' in changes ||
      'facebookAppId' in changes ||
      'shareIcons' in changes ||
      'shareText' in changes ||
      'shareTitle' in changes ||
      'shareUrl' in changes
    ) {
      const baseLink = this.shareServiceHelper.buildShareUrl(
        this.shareUrl,
        this.excludeShareTracking,
      );
      const shareUrls = this.createShareUrls(
        baseLink,
        this.shareTitle,
        this.shareText,
      );
      this.shareButtons = this.shareIcons.map(shareIcon => ({
        // Name must match the generated CSS class name
        buttonClassName: `ShareButton${LIST_OF_SOCIAL_MEDIA_INFO[shareIcon].className}`,
        displayText: LIST_OF_SOCIAL_MEDIA_INFO[shareIcon].text,
        // Name must match the generated CSS class name
        iconClassName: `ShareIcon${LIST_OF_SOCIAL_MEDIA_INFO[shareIcon].className}`,
        iconName: LIST_OF_SOCIAL_MEDIA_INFO[shareIcon].iconName,
        isCopyLink: LIST_OF_SOCIAL_MEDIA_INFO[shareIcon].isCopyLink ?? false,
        labelText: LIST_OF_SOCIAL_MEDIA_INFO[shareIcon].labelText,
        shareUrl: shareUrls[shareIcon],
      }));
    }

    if ('textColor' in changes) {
      this.computeClasses();
    }
  }

  private computeClasses(): void {
    this.shareTextClasses = textVariants({
      color: this.textColor,
      size: TextSize.BODY_16,
      align: TextAlign.CENTER,
    });
  }

  getIconSize(input: ShareButtonSizes): IconSize {
    switch (input) {
      case 'mid':
        return IconSize.MID;
      case 'large':
        return IconSize.LARGE;
      default:
        return IconSize.MID;
    }
  }

  getMarginClassString(): string {
    const classes = ['Share-button'];

    const breakpoints = [
      { prefix: '', value: this.buttonRightMargin },
      { prefix: 'tablet:', value: this.buttonRightTabletMargin },
      { prefix: 'desktop:', value: this.buttonRightDesktopMargin },
      { prefix: 'desktop_large:', value: this.buttonRightDesktopLargeMargin },
    ];

    for (const { prefix, value } of breakpoints) {
      if (value) classes.push(`${prefix}mr-${value}`);
    }

    return classes.join(' ');
  }

  private createShareUrls(
    baseUrl: string,
    shareTitle?: string,
    shareText?: string,
  ): ShareUrlDictionary {
    const encodedLink = encodeURIComponent(baseUrl);
    const encodedTitle = shareTitle
      ? encodeURIComponent(shareTitle)
      : undefined;
    const encondedText = shareText ? encodeURIComponent(shareText) : undefined;

    return {
      facebook: `http://www.facebook.com/share.php?u=${encodedLink}`,
      linkedin: `http://www.linkedin.com/shareArticle?mini=true&url=${encodedLink}&title=${encodedTitle}&source=https://www.freelancer.com`,
      reddit: `https://reddit.com/submit?url=${encodedLink}&title=${encodedTitle}`,
      telegram: `https://t.me/share/url?url=${encodedLink}&text=${encondedText}`,
      twitter: `https://twitter.com/intent/tweet?text=${encondedText}: ${encodedLink}`,
      messenger: this.facebookAppId
        ? `https://www.facebook.com/dialog/send?app_id=${this.facebookAppId}&link=${encodedLink}&redirect_uri=${encodedLink}`
        : baseUrl,
      // The copy-link button copies the raw link rather than navigating.
      copyLink: baseUrl,
      // pinterest: `https://pinterest.com/pin/create/bookmarklet/?url=${encodedLink}`,
    };
  }

  onClick(): void {
    this.onButtonClick.emit();
  }

  onCopyClick(link: string): void {
    this.clipboard.copy(link);
    this.copiedTooltip?.show();
    // Auto-dismiss so touch devices (which never fire mouseleave) don't leave
    // the tooltip stuck open. A standalone timer is used rather than
    // `MatTooltip.hide(delay)` because calling `hide` right after `show`
    // cancels the still-pending show and the tooltip never appears.
    clearTimeout(this.copiedTooltipTimeoutId);
    this.copiedTooltipTimeoutId = this.timeUtils.setTimeout(() => {
      this.copiedTooltip?.hide();
    }, COPIED_TOOLTIP_DURATION_MS);
    this.onCopyLink.emit();
  }

  resetCopiedTooltip(): void {
    clearTimeout(this.copiedTooltipTimeoutId);
    this.copiedTooltip?.hide();
  }

  trackByIconName(_: number, item: ShareButton): string {
    return item.iconName;
  }
}
